import axios, { AxiosInstance, AxiosError } from 'axios';
import keycloak from './keycloak';
import { requiredEnv, optionalEnv } from '../lib/env';

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

interface TokenInfo {
  token: string;
  expiresAt: number;
  refreshToken?: string;
}

interface ErrorResponse {
  error?: string;
  error_description?: string;
}

class TokenManager {
  private sensediaTokenInfo: TokenInfo | null = null;
  private refreshPromises = new Map<string, Promise<void>>();
  private readonly BUFFER_TIME_MS = 30 * 1000; // 30 segundos antes da expiração

  constructor() {
    this.setupKeycloakListeners();
  }

  private setupKeycloakListeners() {
    keycloak.onTokenExpired = () => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Keycloak token expirado - tentando renovar automaticamente');
      }
      // Tenta renovar automaticamente em background
      this.getKeycloakToken(true).catch(() => {
        // Falha silenciosa - será tratada na próxima requisição
      });
    };
    
    keycloak.onAuthLogout = () => {
      this.clearAllTokens();
    };
  }

  // Keycloak Token Management
  async getKeycloakToken(forceRefresh = false): Promise<string | null> {
    try {
      // Verifica se o Keycloak está inicializado e autenticado
      if (!keycloak.authenticated) {
        return null;
      }

      const minValidity = forceRefresh ? 0 : 30;
      
      // Retry simples para falhas temporárias
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const refreshed = await keycloak.updateToken(minValidity);
          
          if (refreshed && process.env.NODE_ENV !== 'production') {
            console.debug('Keycloak token renovado');
          }
          
          return keycloak.token || null;
        } catch (error) {
          lastError = error;
          if (attempt === 0) {
            // Aguarda 500ms antes do retry
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      throw lastError;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Token Keycloak não disponível após retry');
      }
      return null;
    }
  }

  // Sensedia Token Management
  private saveTokenInfo(tokenData: TokenResponse): void {
    this.sensediaTokenInfo = {
      token: tokenData.access_token,
      expiresAt: Date.now() + (tokenData.expires_in * 1000),
      refreshToken: tokenData.refresh_token
    };
  }

  private isTokenExpired(tokenInfo: TokenInfo): boolean {
    return Date.now() >= (tokenInfo.expiresAt - this.BUFFER_TIME_MS);
  }

  private async refreshSensediaToken(): Promise<void> {
    const refreshKey = 'sensedia';
    
    if (this.refreshPromises.has(refreshKey)) {
      await this.refreshPromises.get(refreshKey);
      return;
    }

    const refreshPromise = this.doRefreshSensediaToken();
    this.refreshPromises.set(refreshKey, refreshPromise);
    
    try {
      await refreshPromise;
    } finally {
      this.refreshPromises.delete(refreshKey);
    }
  }

  private async doRefreshSensediaToken(): Promise<void> {
    try {
      if (this.sensediaTokenInfo?.refreshToken) {
        await this.refreshWithRefreshToken();
      } else {
        await this.getNewSensediaToken();
      }
    } catch (error) {
      this.sensediaTokenInfo = null;
      throw error;
    }
  }

  private async refreshWithRefreshToken(): Promise<void> {
    try {
      const response = await axios.post<TokenResponse>(
        requiredEnv('VITE_SENS_AUTH_TOKEN_URI'),
        {
          grant_type: 'refresh_token',
          refresh_token: this.sensediaTokenInfo!.refreshToken,
        },
        {
          headers: {
            'Authorization': this.getBasicAuthHeader(),
          },
        }
      );
      
      this.saveTokenInfo(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const isInvalidRefreshToken = 
        axiosError.response?.status === 400 &&
        axiosError.response?.data?.errors?.some(
          (err: any) => err.type === 'INVALID' && err.message?.includes('Invalid Refresh Token')
        );

      if (isInvalidRefreshToken) {
        this.sensediaTokenInfo = null;
        await this.getNewSensediaToken();
        return;
      }

      this.sensediaTokenInfo = null;
      throw error;
    }
  }

  private async getNewSensediaToken(): Promise<void> {
    // Grant code flow (mantendo o que funcionava)
    const grantResponse = await axios.post(
      requiredEnv('VITE_SENS_AUTH_GRANT_URI'),
      {
        client_id: requiredEnv('VITE_SENS_CLIENT_ID'),
        extraInfo: {},
        redirect_uri: requiredEnv('VITE_SENS_REDIRECT_URI'),
        state: 'string',
      },
      {
        headers: {
          'Authorization': this.getBasicAuthHeader(),
        },
      }
    );

    const code = new URLSearchParams(grantResponse.data.redirect_uri.split('?')[1]).get('code');
    if (!code) throw new Error('Authorization code não encontrado');

    const tokenResponse = await axios.post<TokenResponse>(
      requiredEnv('VITE_SENS_AUTH_TOKEN_URI'),
      {
        grant_type: 'authorization_code',
        code,
      },
      {
        headers: {
          'Authorization': this.getBasicAuthHeader(),
        },
      }
    );

    this.saveTokenInfo(tokenResponse.data);
  }

  private getBasicAuthHeader(): string {
    const credentials = btoa(`${requiredEnv('VITE_SENS_CLIENT_ID')}:${requiredEnv('VITE_SENS_PASS')}`);
    return `Basic ${credentials}`;
  }

  async getSensediaToken(): Promise<string> {
    if (optionalEnv('VITE_AMBIENTE') === 'local') {
      return '';
    }

    if (!this.sensediaTokenInfo || this.isTokenExpired(this.sensediaTokenInfo)) {
      await this.refreshSensediaToken();
    }

    if (!this.sensediaTokenInfo) {
      throw new Error('Não foi possível obter token Sensedia');
    }

    return this.sensediaTokenInfo.token;
  }

  // Axios Interceptors Setup
  setupAxiosInterceptors(apiInstance: AxiosInstance, includeKeycloak = true): void {
    this.setupRequestInterceptor(apiInstance, includeKeycloak);
    this.setupResponseInterceptor(apiInstance, includeKeycloak);
  }

  private setupRequestInterceptor(apiInstance: AxiosInstance, includeKeycloak: boolean): void {
    apiInstance.interceptors.request.use(
      async (config) => {
        // Sensedia Token
        const sensediaToken = await this.getSensediaToken();
        if (sensediaToken) {
          config.headers.Authorization = `Bearer ${sensediaToken}`;
        }

        // Keycloak Token
        if (includeKeycloak) {
          const keycloakToken = await this.getKeycloakToken();
          if (keycloakToken) {
            config.headers.AuthorizationAC = `Bearer ${keycloakToken}`;
          }
        }

        return config;
      },
      (error) => {
        console.error('Erro no request interceptor:', error);
        return Promise.reject(error);
      }
    );
  }

  private setupResponseInterceptor(apiInstance: AxiosInstance, includeKeycloak: boolean): void {
    apiInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (this.isRetryableAuthError(error)) {
          return this.handleRetryableAuthError(apiInstance, error, includeKeycloak);
        }

        if (this.isFatalAuthError(error)) {
          this.handleFatalAuthError();
        }

        return Promise.reject(error);
      }
    );
  }

  private isRetryableAuthError(error: AxiosError): boolean {
    const originalRequest = error.config as any;
    return error.response?.status === 401 && !originalRequest?._retry;
  }

  private isFatalAuthError(error: AxiosError): boolean {
    const data = error.response?.data as any;
    
    const isInvalidGrant = 
      error.response?.status === 400 &&
      data?.error === 'invalid_grant' &&
      data?.error_description === 'Token is not active';

    const isInvalidRefreshToken = 
      error.response?.status === 400 &&
      data?.errors?.some(
        (err: any) => err.type === 'INVALID' && err.message?.includes('Invalid Refresh Token')
      );

    return isInvalidGrant || isInvalidRefreshToken;
  }

  private isFatalKeycloakError(error: any): boolean {
    const errorMessage = error?.message || error?.error || '';
    return (
      errorMessage.includes('invalid_grant') ||
      errorMessage.includes('invalid_token') ||
      errorMessage.includes('session_not_active')
    );
  }

  private async handleRetryableAuthError(
    apiInstance: AxiosInstance, 
    error: AxiosError, 
    includeKeycloak: boolean
  ): Promise<any> {
    const originalRequest = error.config as any;
    originalRequest._retry = true;

    try {
      // Refresh tokens independentemente
      let sensediaSuccess = false;
      let keycloakSuccess = false;
      
      try {
        await this.refreshSensediaToken();
        const sensediaToken = await this.getSensediaToken();
        originalRequest.headers.Authorization = `Bearer ${sensediaToken}`;
        sensediaSuccess = true;
      } catch (sensediaError) {
        // Sensedia falhou, mas continua tentando Keycloak
      }

      if (includeKeycloak) {
        try {
          const keycloakToken = await this.getKeycloakToken(true);
          if (keycloakToken) {
            originalRequest.headers.AuthorizationAC = `Bearer ${keycloakToken}`;
            keycloakSuccess = true;
          }
        } catch (keycloakError) {
          // Keycloak falhou, mas se Sensedia funcionou, continua
        }
      } else {
        keycloakSuccess = true; // Não é necessário
      }
      
      // Só falha se ambos falharam ou se o necessário falhou
      if (!sensediaSuccess || (includeKeycloak && !keycloakSuccess)) {
        throw new Error('Falha ao renovar tokens necessários');
      }

      return apiInstance(originalRequest);
    } catch (refreshError) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Falha ao renovar tokens');
      }
      
      // Só faz logout em casos realmente fatais
      if (includeKeycloak && keycloak.authenticated && this.isFatalKeycloakError(refreshError)) {
        this.handleFatalAuthError();
      }
      
      return Promise.reject(refreshError);
    }
  }

  private handleFatalAuthError(): void {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Erro de autenticação fatal. Fazendo logout.');
    }
    this.clearAllTokens();
    if (keycloak.authenticated) {
      keycloak.logout();
    }
  }

  clearAllTokens(): void {
    this.sensediaTokenInfo = null;
    this.refreshPromises.clear();
  }
}

export default new TokenManager();